'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Alert, Badge, Button, DropdownMenu, EmptyState, EntregaCell, type EntregaInfo, IconButton, LazyLoader, StatsCard, Table, TableCard, TextField, Tooltip } from '@/shared/components/ui';
import * as SubframeCore from '@subframe/core';
import { FeatherArrowRight, FeatherCheckCircle, FeatherDollarSign, FeatherDownload, FeatherEye, FeatherInfo, FeatherLayers, FeatherListChecks, FeatherMoreVertical, FeatherSearch, FeatherShieldOff, FeatherTrendingUp, FeatherX } from '@subframe/core';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { formatCurrency } from '@/shared/formatters';
import { flexionar, pluralizar } from '@/shared/utils/text';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { exportNegociacaoCasosCsv, listarLotesNegociacao } from '../../store/actions';
import { selectLotes, selectLotesDashboard, selectLotesError, selectLotesLoading, selectLotesPagination, selectNegociacaoExportingCsv } from '../../store/selectors';
import { useNegociacaoLoteStatusEvents } from '../../hooks/useNegociacaoLoteStatusEvents';
import type { LoteListItem, NegociacaoLoteListStatus } from '../../types/lotes.types';
import { useToast } from '@/shared/providers';
const ITEMS_PER_PAGE = 10;
const SEARCH_DEBOUNCE_MS = 400;
const STATUS_OPTIONS: {
  label: string;
  value: NegociacaoLoteListStatus;
}[] = [{
  label: 'Pendente',
  value: 'PENDING'
}, {
  label: 'Validando',
  value: 'VALIDATING'
}, {
  label: 'Ativo',
  value: 'PROCESSING'
}, {
  label: 'Concluído',
  value: 'COMPLETED'
}, {
  label: 'Falhou',
  value: 'FAILED'
}, {
  label: 'Cancelado',
  value: 'CANCELED'
}];
const getStatusBadgeVariant = (status: NegociacaoLoteListStatus): 'success' | 'warning' | 'neutral' | 'error' | 'brand' => {
  switch (status) {
    case 'PROCESSING':
      return 'brand';
    case 'VALIDATING':
      return 'warning';
    case 'COMPLETED':
      return 'success';
    case 'FAILED':
      return 'error';
    case 'PENDING':
    case 'CANCELED':
    default:
      return 'neutral';
  }
};
const getStatusLabel = (status: NegociacaoLoteListStatus): string => {
  const opt = STATUS_OPTIONS.find(o => o.value === status);
  return opt?.label ?? status;
};
function getEntregaInfoNegociacao(lote: LoteListItem): EntregaInfo {
  const total = lote.itensTotal ?? 0;
  const entregues = lote.entregues ?? 0;
  if (lote.status === 'VALIDATING') return {
    kind: 'aguardandoValidacao'
  };
  if (lote.status === 'PENDING') {
    return total === 0 ? {
      kind: 'vazio'
    } : {
      kind: 'aguardando'
    };
  }
  if (lote.status === 'FAILED' || lote.status === 'CANCELED') {
    return total === 0 ? {
      kind: 'vazio'
    } : {
      kind: 'encerrado',
      entregues,
      total
    };
  }
  if (total === 0) return {
    kind: 'vazio'
  };
  if (lote.pendencias > 0) return {
    kind: 'parcial',
    entregues,
    total
  };
  return entregues >= total ? {
    kind: 'completo',
    entregues,
    total
  } : {
    kind: 'parcial',
    entregues,
    total
  };
}
function ProcessingSSEWatcher(t0) {
  const $ = _c(2);
  const {
    loteId,
    onStatusChange
  } = t0;
  let t1;
  if ($[0] !== onStatusChange) {
    t1 = {
      enabled: true,
      onComplete: onStatusChange
    };
    $[0] = onStatusChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  useNegociacaoLoteStatusEvents(loteId, t1);
  return null;
}
export function NegociacoesLotesView() {
  const $ = _c(124);
  const router = useRouter();
  const dispatch = useAppDispatch();
  const {
    showError,
    showSuccess,
    showWarning
  } = useToast();
  const lotes = useAppSelector(selectLotes);
  const dashboard = useAppSelector(selectLotesDashboard);
  const loading = useAppSelector(selectLotesLoading);
  const lotesError = useAppSelector(selectLotesError);
  const pagination = useAppSelector(selectLotesPagination);
  const isExportingCsv = useAppSelector(selectNegociacaoExportingCsv);
  let t0;
  if ($[0] !== dispatch || $[1] !== showError || $[2] !== showSuccess || $[3] !== showWarning) {
    t0 = async (loteId, format$0) => {
      const result = await dispatch(exportNegociacaoCasosCsv({
        loteId,
        format: format$0
      }));
      if (exportNegociacaoCasosCsv.fulfilled.match(result)) {
        if (result.payload.truncated) {
          showWarning("Exporta\xE7\xE3o parcial", "Limite m\xE1ximo de linhas atingido. Refine os filtros para incluir todos os registros.");
          return;
        }
        showSuccess(`${format$0 === "xlsx" ? "Excel" : "CSV"} exportado`, "A planilha do lote foi gerada com sucesso.");
        return;
      }
      const message = typeof result.payload === "string" ? result.payload : result.error?.message || "Erro ao exportar";
      showError("Erro", message);
    };
    $[0] = dispatch;
    $[1] = showError;
    $[2] = showSuccess;
    $[3] = showWarning;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const handleExportLote = t0;
  const [search, setSearch] = useState("");
  const [pendenciaDismissed, setPendenciaDismissed] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const debounceRef = useRef(null);
  let t1;
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
    t2 = [];
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[7] !== currentPage || $[8] !== debouncedSearch || $[9] !== dispatch || $[10] !== statusFilter) {
    t3 = () => {
      dispatch(listarLotesNegociacao({
        status: statusFilter,
        search: debouncedSearch || undefined,
        page: currentPage,
        limit: ITEMS_PER_PAGE
      }));
    };
    t4 = [dispatch, statusFilter, currentPage, debouncedSearch];
    $[7] = currentPage;
    $[8] = debouncedSearch;
    $[9] = dispatch;
    $[10] = statusFilter;
    $[11] = t3;
    $[12] = t4;
  } else {
    t3 = $[11];
    t4 = $[12];
  }
  useEffect(t3, t4);
  let t5;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = value => {
      setSearch(value);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        setDebouncedSearch(value);
        setCurrentPage(1);
      }, SEARCH_DEBOUNCE_MS);
    };
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handleSearch = t5;
  let t6;
  if ($[14] !== lotes) {
    t6 = lotes.filter(_temp).map(_temp2);
    $[14] = lotes;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const processingLoteIds = t6;
  const totalContatosPendentes = dashboard.pendencias;
  const showPendenciaBanner = !pendenciaDismissed && totalContatosPendentes > 0;
  let t7;
  if ($[16] !== currentPage || $[17] !== debouncedSearch || $[18] !== dispatch || $[19] !== statusFilter) {
    t7 = () => {
      dispatch(listarLotesNegociacao({
        status: statusFilter,
        search: debouncedSearch || undefined,
        page: currentPage,
        limit: ITEMS_PER_PAGE
      }));
    };
    $[16] = currentPage;
    $[17] = debouncedSearch;
    $[18] = dispatch;
    $[19] = statusFilter;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const handleProcessingComplete = t7;
  let t8;
  if ($[21] !== router) {
    t8 = () => {
      router.push("/negociacoes/ajustes");
    };
    $[21] = router;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const handleAjustarPendencias = t8;
  let t9;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = value_0 => {
      setStatusFilter(value_0);
      setCurrentPage(1);
    };
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const handleStatusFilter = t9;
  const maxPage = pagination.totalPages > 0 ? pagination.totalPages : 1;
  const isInitialLoad = loading && lotes.length === 0 && !search && !statusFilter;
  if (isInitialLoad) {
    let t10;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <LazyLoader />;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    return t10;
  }
  if (lotesError && !loading) {
    let t10;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <span className="text-body-bold font-body-bold text-error-600">Erro ao carregar lotes</span>;
      $[25] = t10;
    } else {
      t10 = $[25];
    }
    let t11;
    if ($[26] !== lotesError) {
      t11 = <span className="text-body font-body text-subtext-color">{lotesError}</span>;
      $[26] = lotesError;
      $[27] = t11;
    } else {
      t11 = $[27];
    }
    let t12;
    if ($[28] !== dispatch) {
      t12 = <Button variant="neutral-secondary" onClick={() => void dispatch(listarLotesNegociacao({
        page: 1,
        limit: ITEMS_PER_PAGE
      }))}>Tentar novamente</Button>;
      $[28] = dispatch;
      $[29] = t12;
    } else {
      t12 = $[29];
    }
    let t13;
    if ($[30] !== t11 || $[31] !== t12) {
      t13 = <div className="flex w-full flex-col items-center justify-center gap-4 py-16">{t10}{t11}{t12}</div>;
      $[30] = t11;
      $[31] = t12;
      $[32] = t13;
    } else {
      t13 = $[32];
    }
    return t13;
  }
  let t10;
  if ($[33] !== handleProcessingComplete || $[34] !== processingLoteIds) {
    let t11;
    if ($[36] !== handleProcessingComplete) {
      t11 = id => <ProcessingSSEWatcher key={id} loteId={id} onStatusChange={handleProcessingComplete} />;
      $[36] = handleProcessingComplete;
      $[37] = t11;
    } else {
      t11 = $[37];
    }
    t10 = processingLoteIds.map(t11);
    $[33] = handleProcessingComplete;
    $[34] = processingLoteIds;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[38] !== handleAjustarPendencias || $[39] !== showPendenciaBanner || $[40] !== totalContatosPendentes) {
    t11 = showPendenciaBanner && <Alert variant="brand" icon={<FeatherListChecks />} title={`${pluralizar(totalContatosPendentes, "envio")} não ${flexionar(totalContatosPendentes, "p\xF4de", "puderam")} ser ${flexionar(totalContatosPendentes, "entregue", "entregues")}`} description={<>{pluralizar(totalContatosPendentes, "contato")}{" "}{flexionar(totalContatosPendentes, "precisa", "precisam")} de um ajuste, somando todos os lotes do workspace. Nada foi cobrado por esses envios — você só paga pelas mensagens efetivamente entregues.<span className="mt-3 flex flex-wrap items-center gap-2.5"><Button size="medium" className="px-3.5" iconRight={<FeatherArrowRight />} onClick={handleAjustarPendencias}>Ajustar</Button><span className="bg-default-background border-success-200 text-success-600 flex items-center gap-1.5 rounded-full border border-solid px-2.5 py-1.5"><FeatherCheckCircle className="h-3.5 w-3.5 shrink-0" /><span className="text-caption font-caption-bold">Sem cobrança pelos envios que falharam</span></span></span></>} actions={<IconButton variant="neutral-tertiary" icon={<FeatherX />} size="small" aria-label={"Dispensar aviso de pend\xEAncias"} onClick={() => setPendenciaDismissed(true)} />} />;
    $[38] = handleAjustarPendencias;
    $[39] = showPendenciaBanner;
    $[40] = totalContatosPendentes;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  let t12;
  if ($[42] !== dashboard.totalLotes) {
    t12 = dashboard.totalLotes.toString();
    $[42] = dashboard.totalLotes;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  let t13;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <FeatherLayers className="text-brand-600 h-4 w-4" />;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let t14;
  if ($[45] !== t12) {
    t14 = <StatsCard label="Total de Lotes" value={t12} icon={t13} />;
    $[45] = t12;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  let t15;
  if ($[47] !== dashboard.taxaAcordoMedia) {
    t15 = dashboard.taxaAcordoMedia.toFixed(1);
    $[47] = dashboard.taxaAcordoMedia;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  const t16 = `${t15}%`;
  let t17;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <FeatherTrendingUp className="text-success-600 h-4 w-4" />;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== t16) {
    t18 = <StatsCard label={"Taxa de Acordo M\xE9dia"} value={t16} icon={t17} trend="neutral" trendLabel={"m\xE9dia entre todos os lotes"} />;
    $[50] = t16;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== dashboard.valorEmNegociacao) {
    t19 = formatCurrency(dashboard.valorEmNegociacao, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    $[52] = dashboard.valorEmNegociacao;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <FeatherDollarSign className="text-brand-600 h-4 w-4" />;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  if ($[55] !== t19) {
    t21 = <StatsCard label={"Valor em Negocia\xE7\xE3o"} value={t19} icon={t20} trend="neutral" trendLabel={"soma do valor em negocia\xE7\xE3o de todos os casos"} />;
    $[55] = t19;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== dashboard.totalBloqueados) {
    t22 = dashboard.totalBloqueados.toString();
    $[57] = dashboard.totalBloqueados;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  let t23;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <FeatherShieldOff className="text-error-600 h-4 w-4" />;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  let t24;
  if ($[60] !== t22) {
    t24 = <StatsCard label="Total de Bloqueados" value={t22} icon={t23} trend="neutral" trendLabel={"barrados pela verifica\xE7\xE3o de WhatsApp"} />;
    $[60] = t22;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== t14 || $[63] !== t18 || $[64] !== t21 || $[65] !== t24) {
    t25 = <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">{t14}{t18}{t21}{t24}</div>;
    $[62] = t14;
    $[63] = t18;
    $[64] = t21;
    $[65] = t24;
    $[66] = t25;
  } else {
    t25 = $[66];
  }
  let t26;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <FeatherSearch />;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  let t27;
  if ($[68] !== search) {
    t27 = search ? <IconButton variant="neutral-tertiary" icon={<FeatherX />} onClick={() => handleSearch("")} size="small" /> : null;
    $[68] = search;
    $[69] = t27;
  } else {
    t27 = $[69];
  }
  let t28;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = e => handleSearch(e.target.value);
    $[70] = t28;
  } else {
    t28 = $[70];
  }
  let t29;
  if ($[71] !== search) {
    t29 = <TextField.Input placeholder="Buscar por nome do lote..." value={search} onChange={t28} />;
    $[71] = search;
    $[72] = t29;
  } else {
    t29 = $[72];
  }
  let t30;
  if ($[73] !== t27 || $[74] !== t29) {
    t30 = <div className="flex w-full flex-col justify-end gap-3 sm:flex-row sm:items-center"><div className="w-full max-w-sm"><TextField variant="outline" icon={t26} iconRight={t27} className="w-full">{t29}</TextField></div></div>;
    $[73] = t27;
    $[74] = t29;
    $[75] = t30;
  } else {
    t30 = $[75];
  }
  let t31;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <span className="text-caption font-caption text-subtext-color">Status:</span>;
    $[76] = t31;
  } else {
    t31 = $[76];
  }
  const t32 = statusFilter === undefined ? "brand" : "neutral";
  let t33;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = () => handleStatusFilter(undefined);
    $[77] = t33;
  } else {
    t33 = $[77];
  }
  let t34;
  if ($[78] !== t32) {
    t34 = <Badge variant={t32} className="cursor-pointer transition-colors" onClick={t33}>Todos</Badge>;
    $[78] = t32;
    $[79] = t34;
  } else {
    t34 = $[79];
  }
  let t35;
  if ($[80] !== statusFilter) {
    t35 = STATUS_OPTIONS.map(opt => <Badge key={opt.value} variant={statusFilter === opt.value ? "brand" : "neutral"} className="hover:border-brand-100 hover:bg-brand-100 cursor-pointer transition-colors" onClick={() => handleStatusFilter(opt.value)}>{opt.label}</Badge>);
    $[80] = statusFilter;
    $[81] = t35;
  } else {
    t35 = $[81];
  }
  let t36;
  if ($[82] !== t34 || $[83] !== t35) {
    t36 = <div className="flex flex-wrap items-center gap-2">{t31}{t34}{t35}</div>;
    $[82] = t34;
    $[83] = t35;
    $[84] = t36;
  } else {
    t36 = $[84];
  }
  let t37;
  if ($[85] !== t30 || $[86] !== t36) {
    t37 = <div className="flex w-full flex-col gap-4">{t30}{t36}</div>;
    $[85] = t30;
    $[86] = t36;
    $[87] = t37;
  } else {
    t37 = $[87];
  }
  const t38 = pagination.total === 1 ? "lote" : "lotes";
  let t39;
  if ($[88] !== pagination.total || $[89] !== t38) {
    t39 = <TableCard.Header title={"Lotes de Negocia\xE7\xE3o"} badge={<Badge variant="neutral">{pagination.total} {t38}</Badge>} />;
    $[88] = pagination.total;
    $[89] = t38;
    $[90] = t39;
  } else {
    t39 = $[90];
  }
  let t40;
  let t41;
  let t42;
  let t43;
  let t44;
  let t45;
  let t46;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <Table.HeaderCell className="w-[18%] min-w-[200px] px-6">Nome do Lote</Table.HeaderCell>;
    t41 = <Table.HeaderCell className="w-[10%] min-w-[120px] px-6">Agente</Table.HeaderCell>;
    t42 = <Table.HeaderCell className="w-[6%] px-6 text-right">Casos</Table.HeaderCell>;
    t43 = <Table.HeaderCell className="w-[9%] px-6 text-right">Taxa de Acordo</Table.HeaderCell>;
    t44 = <Table.HeaderCell className="w-[12%] px-6 text-right">Valor Total</Table.HeaderCell>;
    t45 = <Table.HeaderCell className="w-[10%] px-6">Criado em</Table.HeaderCell>;
    t46 = <Table.HeaderCell className="w-[9%] min-w-[100px] px-6">Status</Table.HeaderCell>;
    $[91] = t40;
    $[92] = t41;
    $[93] = t42;
    $[94] = t43;
    $[95] = t44;
    $[96] = t45;
    $[97] = t46;
  } else {
    t40 = $[91];
    t41 = $[92];
    t42 = $[93];
    t43 = $[94];
    t44 = $[95];
    t45 = $[96];
    t46 = $[97];
  }
  let t47;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <SubframeCore.Tooltip.Trigger asChild={true}><FeatherInfo className="h-3.5 w-3.5 cursor-help text-neutral-400 transition-colors hover:text-neutral-600" /></SubframeCore.Tooltip.Trigger>;
    $[98] = t47;
  } else {
    t47 = $[98];
  }
  let t48;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <Table.HeaderRow>{t40}{t41}{t42}{t43}{t44}{t45}{t46}<Table.HeaderCell className="w-[10%] px-6"><span className="flex items-center gap-1">Entrega<SubframeCore.Tooltip.Provider><SubframeCore.Tooltip.Root>{t47}<SubframeCore.Tooltip.Portal><SubframeCore.Tooltip.Content side="top" align="start" sideOffset={6} asChild={true}><Tooltip className="max-w-[280px]">Validação de entrega do número: confirma se o WhatsApp do contato está ativo antes do disparo.</Tooltip></SubframeCore.Tooltip.Content></SubframeCore.Tooltip.Portal></SubframeCore.Tooltip.Root></SubframeCore.Tooltip.Provider></span></Table.HeaderCell><Table.HeaderCell className="w-[10%] px-6 text-right">Ações</Table.HeaderCell></Table.HeaderRow>;
    $[99] = t48;
  } else {
    t48 = $[99];
  }
  let t49;
  if ($[100] !== handleExportLote || $[101] !== isExportingCsv || $[102] !== loading || $[103] !== lotes || $[104] !== router || $[105] !== search || $[106] !== statusFilter) {
    t49 = loading ? <Table.Row><Table.Cell colSpan={9} className="py-16"><LazyLoader /></Table.Cell></Table.Row> : lotes.length === 0 ? <Table.Row><Table.Cell colSpan={9} className="py-32"><EmptyState icon={<FeatherSearch />} title="Nenhum lote encontrado" description="Nenhum lote encontrado para os filtros selecionados." action={search || statusFilter ? <Button variant="neutral-secondary" onClick={() => {
          handleSearch("");
          handleStatusFilter(undefined);
        }}>Limpar filtros</Button> : undefined} /></Table.Cell></Table.Row> : lotes.map(lote => <Table.Row key={lote.id} className="min-h-[67px] cursor-pointer transition-colors hover:bg-neutral-50" onClick={() => router.push(`/negociacoes/lotes/${lote.id}`)}><Table.Cell className="min-h-[67px] min-w-[200px] px-6 py-3"><span className="text-body-bold font-body-bold text-default-font line-clamp-1">{lote.title ?? "\u2014"}</span></Table.Cell><Table.Cell className="min-h-[67px] min-w-[120px] px-6 py-3"><span className="text-body font-body text-default-font line-clamp-1">{lote.flow_name ?? "\u2014"}</span></Table.Cell><Table.Cell className="min-h-[67px] px-6 py-3 text-right"><span className="text-body-bold font-body-bold text-default-font">{lote.totais.totalCasos.toLocaleString("pt-BR")}</span></Table.Cell><Table.Cell className="min-h-[67px] px-6 py-3 text-right"><span className={lote.totais.taxaAcordo >= 70 ? "text-body-bold font-body-bold text-success-600" : lote.totais.taxaAcordo >= 50 ? "text-body-bold font-body-bold text-warning-600" : "text-body-bold font-body-bold text-error-600"}>{lote.status === "CANCELED" || lote.totais.totalCasos === 0 ? "\u2014" : `${lote.totais.taxaAcordo.toFixed(1)}%`}</span></Table.Cell><Table.Cell className="min-h-[67px] px-6 py-3 text-right"><div className="flex flex-col items-end gap-0.5"><span className="text-body-bold font-body-bold text-default-font">{formatCurrency(lote.totais.valorTotal, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            })}</span>{lote.totais.valorAcordado > 0 && <span className="text-caption font-caption text-success-600">{formatCurrency(lote.totais.valorAcordado, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            })}{" "}acordado</span>}</div></Table.Cell><Table.Cell className="min-h-[67px] px-6 py-3"><div className="flex flex-col gap-0.5"><span className="text-body font-body text-default-font">{format(new Date(lote.created_at), "dd/MM/yyyy", {
              locale: ptBR
            })}</span><span className="text-caption font-caption text-subtext-color">{format(new Date(lote.created_at), "HH:mm", {
              locale: ptBR
            })}</span></div></Table.Cell><Table.Cell className="min-h-[67px] min-w-[100px] px-6 py-3"><Badge variant={getStatusBadgeVariant(lote.status)} icon={lote.status === "PROCESSING" || lote.status === "VALIDATING" ? <Loader2 className="h-3 w-3 animate-spin" /> : undefined}>{getStatusLabel(lote.status)}</Badge></Table.Cell><Table.Cell className="min-h-[67px] px-6 py-3"><EntregaCell info={getEntregaInfoNegociacao(lote)} /></Table.Cell><Table.Cell className="min-h-[67px] justify-end px-6 py-3" onClick={_temp3}><div className="flex items-center justify-end"><SubframeCore.DropdownMenu.Root><SubframeCore.DropdownMenu.Trigger asChild={true}><IconButton variant="neutral-tertiary" icon={<FeatherMoreVertical />} size="small" /></SubframeCore.DropdownMenu.Trigger><SubframeCore.DropdownMenu.Portal><SubframeCore.DropdownMenu.Content side="bottom" align="end" sideOffset={8} className="animate-fade-in"><DropdownMenu><DropdownMenu.DropdownItem icon={<FeatherEye />} onClick={() => router.push(`/negociacoes/lotes/${lote.id}`)}>Ver detalhes</DropdownMenu.DropdownItem><DropdownMenu.DropdownItem icon={<FeatherDownload />} disabled={isExportingCsv} onClick={() => void handleExportLote(lote.id, "csv")}>Exportar como CSV</DropdownMenu.DropdownItem><DropdownMenu.DropdownItem icon={<FeatherDownload />} disabled={isExportingCsv} onClick={() => void handleExportLote(lote.id, "xlsx")}>Exportar como Excel (.xlsx)</DropdownMenu.DropdownItem></DropdownMenu></SubframeCore.DropdownMenu.Content></SubframeCore.DropdownMenu.Portal></SubframeCore.DropdownMenu.Root></div></Table.Cell></Table.Row>);
    $[100] = handleExportLote;
    $[101] = isExportingCsv;
    $[102] = loading;
    $[103] = lotes;
    $[104] = router;
    $[105] = search;
    $[106] = statusFilter;
    $[107] = t49;
  } else {
    t49 = $[107];
  }
  let t50;
  if ($[108] !== t49) {
    t50 = <div className="overflow-x-auto"><Table header={t48}>{t49}</Table></div>;
    $[108] = t49;
    $[109] = t50;
  } else {
    t50 = $[109];
  }
  let t51;
  if ($[110] !== currentPage || $[111] !== loading || $[112] !== maxPage) {
    t51 = maxPage > 1 && <TableCard.Footer><div className="flex items-center justify-between"><div className="flex items-center gap-2"><Button variant="neutral-secondary" size="small" icon={<ChevronLeft className="h-4 w-4" />} disabled={loading || currentPage <= 1} onClick={() => setCurrentPage(_temp4)}>Anterior</Button><Button variant="neutral-secondary" size="small" iconRight={<ChevronRight className="h-4 w-4" />} disabled={loading || currentPage >= maxPage} onClick={() => setCurrentPage(p_0 => Math.min(maxPage, p_0 + 1))}>Próximo</Button></div><div className="hidden items-center gap-1 sm:flex">{Array.from({
            length: Math.min(3, maxPage)
          }, (_, i) => {
            let pageNum;
            if (maxPage <= 3) {
              pageNum = i + 1;
            } else {
              if (currentPage <= 2) {
                pageNum = i + 1;
              } else {
                if (currentPage >= maxPage - 1) {
                  pageNum = maxPage - 2 + i;
                } else {
                  pageNum = currentPage - 1 + i;
                }
              }
            }
            return <Button key={pageNum} variant={currentPage === pageNum ? "brand-secondary" : "neutral-tertiary"} size="small" className="h-8 min-w-[32px] p-0" onClick={() => setCurrentPage(pageNum)}>{pageNum}</Button>;
          })}</div><span className="text-caption text-subtext-color">Página {currentPage} de {maxPage}</span></div></TableCard.Footer>;
    $[110] = currentPage;
    $[111] = loading;
    $[112] = maxPage;
    $[113] = t51;
  } else {
    t51 = $[113];
  }
  let t52;
  if ($[114] !== t39 || $[115] !== t50 || $[116] !== t51) {
    t52 = <div className="w-full pb-1"><TableCard>{t39}{t50}{t51}</TableCard></div>;
    $[114] = t39;
    $[115] = t50;
    $[116] = t51;
    $[117] = t52;
  } else {
    t52 = $[117];
  }
  let t53;
  if ($[118] !== t10 || $[119] !== t11 || $[120] !== t25 || $[121] !== t37 || $[122] !== t52) {
    t53 = <div className="flex w-full flex-col gap-6">{t10}{t11}{t25}{t37}{t52}</div>;
    $[118] = t10;
    $[119] = t11;
    $[120] = t25;
    $[121] = t37;
    $[122] = t52;
    $[123] = t53;
  } else {
    t53 = $[123];
  }
  return t53;
}
function _temp4(p) {
  return Math.max(1, p - 1);
}
function _temp3(e_0) {
  return e_0.stopPropagation();
}
function _temp2(l_0) {
  return l_0.id;
}
function _temp(l) {
  return l.status === "PROCESSING" || l.status === "VALIDATING";
}